import { Link, useSearchParams } from 'react-router-dom';
import { Stack } from '@repletoryreach/sdk-ui';
import { PageHeader, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { Banner } from '../../components/panel';
import { useT } from '../../i18n/I18nProvider';
import { WriteTab } from './WriteTab';
import { AskTab } from './AskTab';
import { VisionTab } from './VisionTab';
import { SpeechTab } from './SpeechTab';
import { TranscribeTab } from './TranscribeTab';
import { PicturesTab } from './PicturesTab';
import { SkillsTab } from './SkillsTab';
import { HistoryTab } from './HistoryTab';
import { useAiModels } from './shared';
import './ai.css';

const TABS = ['write', 'ask', 'vision', 'speech', 'transcribe', 'pictures', 'skills', 'history'] as const;
type Tab = (typeof TABS)[number];

/** Speech, transcription and pictures show only once the platform offers a model of that kind. */
const NEEDS: Partial<Record<Tab, 'speech' | 'transcribe' | 'image'>> = { speech: 'speech', transcribe: 'transcribe', pictures: 'image' };

/**
 * AI tools (owner, 14 October 2026: "make it easy for internal and external to choose what they need"): the same
 * tools the AI API offers, for people in the console. Write with a skill, ask your own documents, read a picture,
 * read a text aloud, transcribe a recording, make pictures, save your own skills and see what every AI call cost.
 * Everything is charged to the business's credits, held first, so nothing runs that the credit cannot pay for.
 */
export default function AiToolsPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const models = useAiModels().data?.data ?? [];
  const shown = TABS.filter((x) => !NEEDS[x] || models.some((m) => m.kind === NEEDS[x] && m.priced));
  const tab: Tab = shown.find((x) => x === params.get('tab')) ?? 'write';
  const choose = (next: string | null) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', next ?? 'write'); return n; }, { replace: true });

  return (
    <Stack gap="lg">
      <PageHeader title={t('ai.title')} description={t('ai.description')} withDivider={false} />
      <Banner tone="info">
        {t('ai.banner')} <Link to="/settings/api-keys">{t('ai.banner.keys')}</Link> · <Link to="/developers">{t('nav.developers')}</Link>
      </Banner>
      <div data-guide="ai-tabs">
        <Tabs value={tab} onChange={choose}>
          <Tabs.List>
            {shown.map((x) => <Tabs.Tab key={x} value={x}>{t(`ai.tabs.${x}`)}</Tabs.Tab>)}
          </Tabs.List>
        </Tabs>
      </div>
      {tab === 'write' && <WriteTab />}
      {tab === 'ask' && <AskTab />}
      {tab === 'vision' && <VisionTab />}
      {tab === 'speech' && <SpeechTab />}
      {tab === 'transcribe' && <TranscribeTab />}
      {tab === 'pictures' && <PicturesTab />}
      {tab === 'skills' && <SkillsTab />}
      {tab === 'history' && <HistoryTab />}
    </Stack>
  );
}
